<template>
  <div class="standing-orders">
    <transition name="fade" mode="out-in">
      <loader-with-message v-if="dataLoading"
                           :key="'soloader'"
                           :has-error="dataFetchError"
                           @retry="fetchAllData"/>
      <div v-else
           :key="'socontent'">
        <transition name="fade" mode="out-in">
          <div v-if="!isEditMode" :key="'content'">
            <so-not-eligible v-if="!customerList.length && !ordersList.length"/>
            <so-no-card v-else-if="!paymentCardsList.length"
                        :disabled="editDisabled"
                        @add-card="addPaymentCard"/>
            <so-no-orders v-else-if="!ordersList.length"
                          @add-new-order="createNewSo"/>
            <so-orders-list v-else
                            :orders-list="ordersList"
                            :customer-list="customerList"
                            @add-new-order="createNewSo"
                            @edit-order="editExistingSo"
                            @delete-order="confirmSoDelete"/>
          </div>
          <so-create-edit v-else
                          :selected-order="selectedOrder"
                          :customer-list="customerList"
                          :payment-cards="paymentCardsList"
                          :disabled="editDisabled"
                          @submit-edit="submitOrderEdit"
                          @submit-new="submitNewOrder"
                          @cancel-edit="cancelEditSo"
                          @add-card="addPaymentCard"/>
        </transition>
      </div>
    </transition>

    <bank-form :form-data="bankFormData" ref="bankForm"/>

    <!-- delete confirmation modal -->
    <confirmation-modal :visible="deleteConfirmationVisible"
                        :closeButtonVisible="false"
                        @close="deleteConfirmationVisible = false"
                        :cancel-button="true"
                        confirm-type="warning"
                        size="md"
                        :confirm-title="$t('standingOrder.deleteConfirm.title')"
                        :confirm-description="$t('standingOrder.deleteConfirm.message')"
                        :confirm-text="$t('standingOrder.deleteConfirm.confirmLabel')"
                        @confirm="deleteSo"
                        :button-disabled="deleteButtonDisabled"/>
  </div>
</template>

<script>
import LoaderWithMessage from '@/components/common/loader-with-message'
import SoNoCard from '@/components/bills/so-subcomponents/so-no-card'
import SoNoOrders from '@/components/bills/so-subcomponents/so-no-orders'
import SoNotEligible from '@/components/bills/so-subcomponents/so-not-eligible'
import SoOrdersList from '@/components/bills/so-subcomponents/so-orders-list'
import SoCreateEdit from '@/components/bills/so-subcomponents/so-create-edit'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import BankForm from '@/components/common/payment-cards/bank-form'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import { lsSave } from '@/utils/localStorageController'
import { addDataLayerEvent } from '@/services/dataLayer'

export default {
  name: 'bills-standing-orders',
  components: { ConfirmationModal, BankForm, SoCreateEdit, SoOrdersList, SoNotEligible, SoNoOrders, SoNoCard, LoaderWithMessage },
  data () {
    return {
      dataLoading: true,
      dataFetchError: false,
      ordersList: [],
      paymentCardsList: [],
      customerList: [],
      isEditMode: false,
      selectedOrder: {},
      editDisabled: false,
      bankFormData: {},
      deleteConfirmationVisible: false,
      deleteButtonDisabled: false,
      soCode: ''
    }
  },
  mixins: [errorMessageHandler],
  methods: {
    /**
     * A function that fetches all standing orders
     * Result of the API call should be an array
     */
    getStandingOrders () {
      return this.$apiRequest.post('user/standingorder/valid')
        .then(response => {
          this.ordersList = response.data.mtmCustomers || []
        })
    },
    /**
     * A function that fetches an array of payment cards for current user
     */
    getPaymentCards () {
      return this.$apiRequest.post('user/payment/cards')
        .then(response => {
          this.paymentCardsList = response.data.cards || []
        })
    },
    /**
     * A function that fetches customers eligible for the standing order
     * At least one customer account is needed as a response here, so the user can create new standing orders
     */
    getCustomers () {
      return this.$apiRequest.get('user/standingorder/customers')
        .then(response => {
          this.customerList = response.data.mtmCustomers || []
        })
    },
    /**
     * Fetches all data and hides loader, or shows an error
     */
    fetchAllData () {
      this.dataLoading = true
      const callStack = [this.getStandingOrders(), this.getPaymentCards(), this.getCustomers()]
      Promise.all(callStack)
        .then(() => {
          this.dataLoading = false
        })
        .catch(() => {
          this.dataFetchError = true
        })
    },
    /**
     * Function to show the add new standing order form
     * Toggles the value of variable responsible for showing either list of orders or edit form
     */
    createNewSo () {
      this.isEditMode = true
    },
    /**
     * Empties selectedOrder object and exits edit mode
     */
    cancelEditSo () {
      this.selectedOrder = {}
      this.isEditMode = false
    },
    /**
     * Function that adds the selected standing order object to a local variable
     * that's then passed as prop to so-create-edit component
     * @param data { Object } specific element from standing orders array that's being edited
     */
    editExistingSo (data) {
      this.selectedOrder = { ...data }
      this.isEditMode = true
    },
    /**
     * Saves the code from argument and displays the confirmation dialog
     * @param code { String } code of the standing order
     */
    confirmSoDelete (code = '') {
      this.soCode = code
      this.deleteConfirmationVisible = true
    },
    /**
     * calls delete API with standing order code and displays a toast messages for success and fail cases
     */
    deleteSo () {
      if (!this.soCode) return

      this.deleteButtonDisabled = true
      this.$apiRequest.delete(`user/standingorder?code=${this.soCode}`)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.fetchAllData()

            this.toast('success', {
              title: this.$t('standingOrder.deleteSuccess.title'),
              message: this.$t('standingOrder.deleteSuccess.message')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
          this.editDisabled = false
        })
        .finally(() => {
          this.deleteButtonDisabled = false
          this.deleteConfirmationVisible = false
        })
    },
    /**
     * Submits new order data to API and displays a toast messages for success and fail cases
     * @param data
     */
    submitNewOrder (data) {
      this.editDisabled = true
      this.$apiRequest.post('user/standingorder', data)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.cancelEditSo()
            this.fetchAllData()
            this.editDisabled = false
            addDataLayerEvent('standing_order_created')
            this.toast('success', {
              title: this.$t('standingOrder.edit.createSuccess.title'),
              message: this.$t('standingOrder.edit.createSuccess.message')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
          this.editDisabled = false
        })
    },
    /**
     * Submits edited data for standing order to API and displays a toast messages for success and fail cases
     * @param data
     */
    submitOrderEdit (data) {
      this.editDisabled = true
      this.$apiRequest.put('user/standingorder', data)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.cancelEditSo()
            this.getStandingOrders()
            this.editDisabled = false

            this.toast('success', {
              title: this.$t('standingOrder.edit.editSuccess.title'),
              message: this.$t('standingOrder.edit.editSuccess.message')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
          this.editDisabled = false
        })
    },
    /**
     * Fetches a authorization form from API, fills the hidden form and submits the data to bank
     * Will open a new page in the same window on success
     */
    addPaymentCard () {
      this.editDisabled = true
      lsSave('sc_standingOrderAddCard', true)

      this.$apiRequest.post('user/payment/authform')
        .then(response => {
          this.bankFormData = response.data
          setTimeout(() => {
            this.$refs.bankForm.$refs.submitButton.click()
          }, 100)
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
          this.editDisabled = false
        })
    }
  },
  mounted () {
    this.fetchAllData()
  }
}
</script>

<style scoped lang="scss" src="./bills-comp-styles/bills-standing-orders.scss"></style>
